import {DateTime} from 'luxon';
import {createSelector} from 'reselect';

import {AppParametricSelector, AppSelector} from '../../../helpers/reselectHelpers';
import {FrameTypesEnum} from '../../../models/api/apiTypes';
import {ContactSourcesEnum} from '../../../models/api/contactModel';
import {VisualRecipientModel} from '../../../models/api/visualRecipientModel';
import {getSelectedSideFrameId} from '../../data/localPreferences/localPreferencesSelectors';
import {getUser} from '../../data/teammates/teammatesSelectors';
import {RecentCommunications, RecentCommunicationsState} from './recentCommunicationsState';

/*
 * Selectors.
 */

const getRecentCommunicationsState: AppSelector<RecentCommunicationsState> = (state) =>
  state.metadata.recentCommunications;

export type GetRecentCommunicationsSelector = AppParametricSelector<any, RecentCommunications | undefined>;
export function makeGetRecentCommunications<P>(
  getContactUid: AppParametricSelector<P, string | undefined>,
): GetRecentCommunicationsSelector {
  return createSelector([getRecentCommunicationsState, getContactUid], (state, contactUid) =>
    contactUid ? state[contactUid] : undefined,
  );
}

// A series of checks based on information we already have available in the Redux store
// so that we can avoid making extraneous network requests
export function makeGetShouldFetchRecentConversations<P>(
  getVisualRecipient: AppParametricSelector<P, VisualRecipientModel | undefined>,
  getRecentCommunications: GetRecentCommunicationsSelector,
) {
  return createSelector(
    [getSelectedSideFrameId, getVisualRecipient, getUser, getRecentCommunications],
    (selectedSideFrameId, visualRecipient, user, recentCommunications) => {
      // If P4 is already open to the contacts plugin, we don't want to show the banner
      if (selectedSideFrameId === FrameTypesEnum.CUSTOMER_VIEW) {
        return false;
      }

      // We don't want to show the banner if there's no recipient or if the recipient is a teammate
      if (!visualRecipient || visualRecipient.teammate) {
        return false;
      }

      // We don't want to show the banner if the email domains are the same
      const isEmailSource = checkIsEmailSource(visualRecipient);
      if (isEmailSource) {
        const recipientDomain = computeDomainNameFromEmail(visualRecipient.contact.handle);
        const userDomain = computeDomainNameFromEmail(user.email);
        if (recipientDomain === userDomain) {
          return false;
        }
      }

      // Lastly, if we already have data for this contact from the past day, we don't need to refetch
      if (recentCommunications) {
        const requestedAtDateTime = DateTime.fromMillis(recentCommunications.requestedAt);
        const differenceInDays = requestedAtDateTime.diffNow(['day']).days;
        if (differenceInDays > -1) {
          return false;
        }
      }

      return true;
    },
  );
}

/*
 * Helpers.
 */

const checkIsEmailSource = (visualRecipient: VisualRecipientModel) =>
  visualRecipient.contact.source === ContactSourcesEnum.EMAIL;

const computeDomainNameFromEmail = (email: string) => email.replace(/.*@/, '');
